import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignup, setIsSignup] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);

  const login = useAuthStore((state) => state.login);
  const register = useAuthStore((state) => state.register);
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();

  // Redireciona se já estiver autenticado
  useEffect(() => {
    if (isAuthenticated) {
      if (user?.tenant_id) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/welcome', { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignup) {
        // Registro
        if (password !== passwordConfirmation) {
          setError('As senhas não coincidem');
          setLoading(false);
          return;
        }

        // Validação de senha forte
        const passwordErrors = validatePassword(password);
        if (passwordErrors.length > 0) {
          setFieldErrors({ password: passwordErrors.join(', ') });
          setError(passwordErrors.join(', '));
          setLoading(false);
          return;
        }

        if (password !== passwordConfirmation) {
          setFieldErrors({ passwordConfirmation: 'As senhas não coincidem' });
          setError('As senhas não coincidem');
          setLoading(false);
          return;
        }

        // Limpa erros de campo
        setFieldErrors({});

        const result = await register(name, email, password, passwordConfirmation);

        if (result.success) {
          // Aguarda um pouco para garantir que o store foi atualizado
          setTimeout(() => {
            // Usuário recém-criado não tem tenant, sempre vai para welcome
            navigate('/welcome', { replace: true });
          }, 100);
        } else {
          setError(result.error || 'Erro ao criar conta. Verifique os dados.');
        }
      } else {
        // Login
        const result = await login(email, password);

        if (result.success) {
          // Aguarda um pouco para garantir que o store foi atualizado
          setTimeout(() => {
            const { user } = useAuthStore.getState();
            // Se usuário já tem tenant, vai para dashboard
            // Se não tem tenant, vai para welcome para criar
            if (user?.tenant_id) {
              navigate('/dashboard', { replace: true });
            } else {
              navigate('/welcome', { replace: true });
            }
          }, 100);
        } else {
          setError(result.error || 'Erro ao fazer login. Verifique suas credenciais.');
        }
      }
    } catch (err) {
      setError('Erro inesperado. Tente novamente.');
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Validação de senha forte
  const validatePassword = (pwd) => {
    const errors = [];
    if (pwd.length < 8) {
      errors.push('A senha deve ter no mínimo 8 caracteres');
    }
    if (!/[a-zA-Z]/.test(pwd)) {
      errors.push('A senha deve conter pelo menos uma letra');
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push('A senha deve conter pelo menos um número');
    }
    return errors;
  };

  // Calcula força da senha
  const calculatePasswordStrength = (pwd) => {
    if (!pwd) return 0;
    let strength = 0;
    if (pwd.length >= 8) strength += 1;
    if (pwd.length >= 12) strength += 1;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) strength += 1;
    if (/[0-9]/.test(pwd)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(pwd)) strength += 1;
    return strength;
  };

  // Handler de mudança de senha
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(calculatePasswordStrength(newPassword));
    // Limpa erro do campo ao modificar
    if (fieldErrors.password) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.password;
        return newErrors;
      });
    }
    if (error && error.includes('senha')) {
      setError('');
    }
  };

  // Handler de mudança de confirmação de senha
  const handlePasswordConfirmationChange = (e) => {
    setPasswordConfirmation(e.target.value);
    // Limpa erro do campo ao modificar
    if (fieldErrors.passwordConfirmation) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.passwordConfirmation;
        return newErrors;
      });
    }
    if (error && error.includes('senhas')) {
      setError('');
    }
  };

  const toggleAuthMode = () => {
    setIsSignup(!isSignup);
    setError('');
    setFieldErrors({});
    setEmail('');
    setPassword('');
    setPasswordConfirmation('');
    setName('');
    setPasswordStrength(0);
  };

  return (
    <div className="auth-container">
      {/* Hero Side */}
      <div className="auth-hero">
        <div className="auth-hero-content">
          <div className="auth-logo">H</div>
          <h1>Haklai Church</h1>
          <p>Gerencie seu ministério com eficiência. Células, membros e crescimento em um só lugar.</p>
        </div>
      </div>

      {/* Form Side */}
      <div className="auth-form-side">
        <div className="auth-form-container">
          {/* Header */}
          <div className="auth-form-header">
            <h2>{isSignup ? 'Criar conta' : 'Bem-vindo de volta'}</h2>
            <p>{isSignup ? 'Preencha os dados para começar' : 'Entre na sua conta para continuar'}</p>
          </div>

          {/* Social Login */}
          <div className="social-login">
            <button type="button" className="social-btn" disabled>
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continuar com Google
            </button>
          </div>

          {/* Divider */}
          <div className="auth-divider">
            <span>ou continue com email</span>
          </div>

          {/* Login Form */}
          {!isSignup && (
            <form className="auth-form auth-form-login" onSubmit={handleSubmit}>
              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="username"
                />
              </div>

              <div className="form-group form-group-password">
                <label className="form-label">Senha</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <span
                  className="password-toggle material-symbols-outlined"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: 'pointer' }}
                >
                  {showPassword ? 'visibility' : 'visibility_off'}
                </span>
              </div>

              <div className="forgot-password">
                <a href="#/forgot-password" onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }}>
                  Esqueceu a senha?
                </a>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block btn-lg auth-submit"
                disabled={loading}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </form>
          )}

          {/* Signup Form */}
          {isSignup && (
            <form className="auth-form auth-form-signup" onSubmit={handleSubmit}>
              {error && (
                <div className="alert alert-error">
                  {error}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Nome completo</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Seu nome"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                  minLength={2}
                  autoComplete="name"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="form-group form-group-password">
                <label className="form-label">Senha</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className={`form-input ${fieldErrors.password ? 'error' : ''}`}
                  placeholder="Mínimo 8 caracteres, letras e números"
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  disabled={loading}
                  minLength={8}
                  autoComplete="new-password"
                />
                <span
                  className="password-toggle material-symbols-outlined"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ cursor: 'pointer' }}
                >
                  {showPassword ? 'visibility' : 'visibility_off'}
                </span>
                {fieldErrors.password && (
                  <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {fieldErrors.password}
                  </span>
                )}
                {password && !fieldErrors.password && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '0.25rem' }}>
                      {[1, 2, 3, 4, 5].map((level) => (
                        <div
                          key={level}
                          style={{
                            flex: 1,
                            height: '4px',
                            borderRadius: '2px',
                            background: passwordStrength >= level
                              ? passwordStrength <= 2
                                ? 'var(--danger)'
                                : passwordStrength <= 4
                                  ? 'var(--warning)'
                                  : 'var(--success)'
                              : 'var(--border)',
                            transition: 'all 0.2s ease'
                          }}
                        />
                      ))}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                      {passwordStrength <= 2
                        ? 'Senha fraca'
                        : passwordStrength <= 4
                          ? 'Senha média'
                          : 'Senha forte'}
                    </span>
                  </div>
                )}
              </div>

              <div className="form-group form-group-password">
                <label className="form-label">Confirmar senha</label>
                <input
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  className={`form-input ${fieldErrors.passwordConfirmation ? 'error' : ''}`}
                  placeholder="Repita a senha"
                  value={passwordConfirmation}
                  onChange={handlePasswordConfirmationChange}
                  required
                  disabled={loading}
                  minLength={8}
                  autoComplete="new-password"
                />
                <span
                  className="password-toggle material-symbols-outlined"
                  onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                  style={{ cursor: 'pointer' }}
                >
                  {showPasswordConfirmation ? 'visibility' : 'visibility_off'}
                </span>
                {fieldErrors.passwordConfirmation && (
                  <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {fieldErrors.passwordConfirmation}
                  </span>
                )}
                {passwordConfirmation && password !== passwordConfirmation && !fieldErrors.passwordConfirmation && (
                  <span style={{ color: 'var(--warning)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    As senhas não coincidem
                  </span>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block btn-lg auth-submit"
                disabled={loading}
              >
                {loading ? 'Criando conta...' : 'Criar conta'}
              </button>

              <p className="auth-terms">
                Ao criar uma conta, você concorda com nossos{' '}
                <a href="#">Termos de Uso</a> e{' '}
                <a href="#">Política de Privacidade</a>
              </p>
            </form>
          )}

          {/* Toggle Auth Mode */}
          <div className="auth-toggle">
            {!isSignup ? (
              <p className="login-toggle">
                Não tem uma conta?{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); toggleAuthMode(); }}>
                  Criar conta
                </a>
              </p>
            ) : (
              <p className="signup-toggle">
                Já tem uma conta?{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); toggleAuthMode(); }}>
                  Entrar
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
